import React, { useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";
import { Layout } from "views/common/Layout";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "views/components/ui/Card";
import { Loader2, ArrowRight, Check, Star, Users } from "lucide-react";
import { fetchSubCategory } from "actions/category";

// Helper function to validate URL
const isValidUrl = (urlString) => {
    if (!urlString) return false;
    try {
        const url = new URL(urlString);
        return url.protocol === "http:" || url.protocol === "https:";
    } catch (e) {
        return false;
    }
};

const SubCategoryPage = (props) => {
    const { subcategory } = useParams();
    const history = useHistory();

    useEffect(() => {
        props.fetchSubCategory(subcategory);
    }, [subcategory]);

    const handleToolClick = (toolName) => {
        history.push(`/tool/${toolName}`);
    };

    if (props.isLoading) {
        return (
            <Layout>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex justify-center items-center min-h-[50vh]">
                        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    </div>
                </div>
            </Layout>
        );
    }

    if (!props.subCategory) {
        return (
            <Layout>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="text-center">
                        <h1 className="text-2xl font-bold">
                            Subcategory not found
                        </h1>
                        <p className="mt-2 text-muted-foreground">
                            The requested subcategory does not exist.
                        </p>
                    </div>
                </div>
            </Layout>
        );
    }

    const { subCategory } = props;
    const toolNames = subCategory.tools.map((tool) => tool.name).join(", ");
    const features = Array.isArray(subCategory.features)
        ? subCategory.features.join(", ")
        : "";
    const useCases = Array.isArray(subCategory.useCases)
        ? subCategory.useCases.join(", ")
        : "";

    return (
        <Layout>
            <Helmet>
                <title>{`${subCategory.name} - AI Tools & Solutions | Launch Hero FYI`}</title>
                <meta
                    name="description"
                    content={`Discover the best ${subCategory.name} tools and solutions. ${subCategory.description} Browse through ${subCategory.tools.length} curated tools including ${toolNames}.`}
                />
                <meta
                    name="keywords"
                    content={`${subCategory.name}, ${toolNames}, ${features}, ${useCases}, AI tools, software solutions`}
                />
                <meta
                    property="og:title"
                    content={`${subCategory.name} - AI Tools & Solutions | Launch Hero FYI`}
                />
                <meta
                    property="og:description"
                    content={`Discover the best ${subCategory.name} tools and solutions. ${subCategory.description} Browse through ${subCategory.tools.length} curated tools including ${toolNames}.`}
                />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={window.location.href} />
                <meta name="twitter:card" content="summary" />
                <meta
                    name="twitter:title"
                    content={`${subCategory.name} - AI Tools & Solutions | Launch Hero FYI`}
                />
                <meta
                    name="twitter:description"
                    content={`Discover the best ${subCategory.name} tools and solutions. ${subCategory.description} Browse through ${subCategory.tools.length} curated tools including ${toolNames}.`}
                />
                <link rel="canonical" href={window.location.href} />
            </Helmet>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold tracking-tight">
                        {subCategory.name}
                    </h1>
                    <p className="mt-2 text-muted-foreground">
                        {subCategory.description}
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Content */}
                    <div className="lg:col-span-3 space-y-6">
                        {/*<div className="lg:col-span-2 space-y-6">*/}
                        {/* Tools Grid */}
                        <div>
                            <div>
                                <div className="text-xl font-semibold text-gray-900 mb-6">
                                    Tools ({subCategory.tools.length})
                                </div>
                            </div>
                            <div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {subCategory.tools.map((tool) => (
                                        <div
                                            key={tool.uuid}
                                            className="group p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-500 transition-colors cursor-pointer"
                                            onClick={() =>
                                                handleToolClick(tool.name)
                                            }
                                        >
                                            <div className="flex items-center gap-3 mb-3">
                                                <img
                                                    src={
                                                        tool.logo_url &&
                                                        isValidUrl(
                                                            tool.logo_url
                                                        )
                                                            ? tool.logo_url
                                                            : tool.thumbnail ||
                                                              "https://via.placeholder.com/48"
                                                    }
                                                    alt={tool.name}
                                                    className="w-12 h-12 rounded-lg object-contain bg-gray-50"
                                                    onError={(e) => {
                                                        // If image fails to load, fallback to thumbnail or placeholder
                                                        e.target.src =
                                                            tool.thumbnail ||
                                                            "https://via.placeholder.com/48";
                                                    }}
                                                />
                                                <div>
                                                    <h3 className="font-medium group-hover:text-blue-600 transition-colors">
                                                        {tool.name}
                                                    </h3>
                                                    <p className="text-sm text-gray-500">
                                                        {tool.company}
                                                    </p>
                                                </div>
                                            </div>
                                            <p className="text-sm text-gray-600 mb-3">
                                                {tool.shortDescription}
                                            </p>
                                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                                <ArrowRight className="h-4 w-4 opacity-100 group-hover:opacity-100 transition-opacity" />
                                                <span>View details</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Top 10 Tools */}
                        {/*<Card>
                            <CardHeader>
                                <CardTitle>Top 10 Tools</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b border-gray-200">
                                                <th className="text-left py-3 px-4">
                                                    Rank
                                                </th>
                                                <th className="text-left py-3 px-4">
                                                    Tool
                                                </th>
                                                <th className="text-left py-3 px-4">
                                                    Company
                                                </th>
                                                <th className="text-left py-3 px-4">
                                                    Rating
                                                </th>
                                                <th className="text-left py-3 px-4">
                                                    Features
                                                </th>
                                                <th className="text-left py-3 px-4">
                                                    Users
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {subCategory.tools.map(
                                                (tool, index) => (
                                                    <tr
                                                        key={tool.uuid}
                                                        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                                                        onClick={() =>
                                                            handleToolClick(
                                                                tool.name
                                                            )
                                                        }
                                                    >
                                                        <td className="py-4 px-4 font-medium">
                                                            {index + 1}
                                                        </td>
                                                        <td className="py-4 px-4">
                                                            <div className="flex items-center gap-3">
                                                                <img
                                                                    src={
                                                                        tool.thumbnail
                                                                    }
                                                                    alt={
                                                                        tool.name
                                                                    }
                                                                    className="w-8 h-8 rounded-lg object-cover"
                                                                />
                                                                <div>
                                                                    <div className="font-medium">
                                                                        {
                                                                            tool.name
                                                                        }
                                                                    </div>
                                                                    <div className="text-sm text-gray-500">
                                                                        {
                                                                            tool.shortDescription
                                                                        }
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="py-4 px-4">
                                                            {tool.company}
                                                        </td>
                                                        <td className="py-4 px-4">
                                                            <div className="flex items-center gap-1 text-yellow-500">
                                                                <Star className="h-4 w-4 fill-current" />
                                                                <span className="text-sm font-medium">
                                                                    4.8
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="py-4 px-4">
                                                            <div className="flex flex-wrap gap-1">
                                                                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                                                    Free Plan
                                                                </span>
                                                                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                                                    API
                                                                </span>
                                                                <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                                                                    Multi-language
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="py-4 px-4">
                                                            <div className="flex items-center gap-1 text-gray-500">
                                                                <Users className="h-4 w-4" />
                                                                <span className="text-sm">
                                                                    1M+
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </CardContent>
                        </Card>*/}

                        {/* Features */}
                        {subCategory.features &&
                            Array.isArray(subCategory.features) &&
                            subCategory.features.length > 0 && (
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Features</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            {subCategory.features.map(
                                                (feature, index) => (
                                                    <li
                                                        key={index}
                                                        className="flex items-center gap-2"
                                                    >
                                                        <Check className="h-4 w-4 text-green-500" />
                                                        <span>{feature}</span>
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    </CardContent>
                                </Card>
                            )}

                        {/* Use Cases */}
                        {subCategory.useCases &&
                            Array.isArray(subCategory.useCases) &&
                            subCategory.useCases.length > 0 && (
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Use Cases</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            {subCategory.useCases.map(
                                                (useCase, index) => (
                                                    <li
                                                        key={index}
                                                        className="flex items-center gap-2"
                                                    >
                                                        <Check className="h-4 w-4 text-green-500" />
                                                        <span>{useCase}</span>
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    </CardContent>
                                </Card>
                            )}
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Insights */}
                        {/*subCategory.insights && (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Insights</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-gray-600">
                                        {subCategory.insights}
                                    </p>
                                </CardContent>
                            </Card>
                        )*/}

                        {/* FAQ */}
                        {/*subCategory.faq && subCategory.faq.length > 0 && (
                            <Card>
                                <CardHeader>
                                    <CardTitle>FAQ</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        {subCategory.faq.map((item, index) => (
                                            <div key={index}>
                                                <h4 className="font-medium text-gray-900">
                                                    {item.question}
                                                </h4>
                                                <p className="mt-1 text-gray-600">
                                                    {item.answer}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )*/}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

const mapStateToProps = (state) => ({
    subCategory: state.category.currentSubCategory,
    isLoading: state.category.isLoading,
});

export default connect(mapStateToProps, { fetchSubCategory })(SubCategoryPage);
